import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { FileUploadModal } from "src/ui/containers/dashboard/files/components/FileUploadModal/FileUploadModal";
import { UploadProvider } from "src/ui/containers/dashboard/files/context/UploadContext";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";
import { CollapsibleRow, CollapsibleRows, CollapsibleSummary } from "src/ui/shared/table/CollapsibleRow";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { GetFilesParams } from "@dashboard/files/api/rtkClient";
import { FileInfoWithVersions, normalizeFiles } from "@dashboard/files/utils";
import { FileExplorerHeader } from "@dashboard/shared/components/SidePanel/shared/FileExplorerHeader";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { TagFilter } from "@dashboard/shared/components/TagFilter";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { selectDevicesByGroupId } from "@devices/store/selectors/devices";
import { Group } from "@devices/types";
import { Box, TableRow } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader } from "@shared/table/TableHeader";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import ActionsCell from "./components/ActionsCell";
import { useStyles } from "./FileTab.style";
import { columns, FileNameCell, FileTabTagsCell, SourceCell, useParams } from "./FileTab.utils";
import { GroupDevicesProvider } from "./GroupDevicesContext";

export type GroupFileTabParams = Required<
    Pick<GetFilesParams, "workspaceId" | "sortCriteria" | "sortDirection" | "groups" | "visibilities" | "includeParentFiles">
>;

const FileTab = (props: Group) => {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [params, setParams] = useParams(props.id);

    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);

    const {
        data: files,
        isFetching,
        isLoading,
        isError,
        refetch,
    } = useAllCurrentWorkspaceFiles(params, { preferCacheValue: false });
    const data = React.useMemo(() => normalizeFiles(files), [files]);
    const tableLoading = useTableFetchLoading(isFetching, isLoading, params);

    const { data: workspace } = useCurrentWorkspace();

    // Only the devices whose license sits directly in this group, which is exactly what a group
    // command reaches — subgroups are not included on that path.
    const devices = useSelector((state) => selectDevicesByGroupId(state, props.id));

    const { groupAbility } = PermissionService();
    const mayManageFile = groupAbility(props, workspace).can("manage", "File");

    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");
    const isExpired = useCurrentSubscriptionExpired();

    const onIncludeParentFilesChange = (value: boolean) => {
        setParams((prev) => ({ ...prev, includeParentFiles: value }));
    };

    const table = useReactTable<FileInfoWithVersions>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta: { selectedGroupId: props.id },
    });

    const getAddBtnTitle = () => {
        const result = { title: t("sidepanels:groupPanel.tooltip.uploadFile") };

        if (isExpired) {
            result.title = t("sidepanels:groupPanel.tooltip.subscriptionExpired");
        }
        if (!mayManageFile) {
            result.title = t("sidepanels:groupPanel.tooltip.uploadFileError");
        }
        if (isOverlimit) {
            result.title = t("sidepanels:groupPanel.tooltip.uploadFileLimit");
        }
        return result;
    };

    const onSort = (id: string) => {
        setParams((prev) => ({
            ...prev,
            sortCriteria: id,
            sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
        }));
    };

    return (
        <UploadProvider>
            <GroupDevicesProvider devices={devices}>
                <PaneElement.Container>
                    <Box className={classes.headerBox}>
                        <PaneElement.Header>{t("sidepanels:groupPanel.filesTab.availableFiles")}</PaneElement.Header>
                        <SecondaryButton
                            onClick={() => setIsDialogOpen(true)}
                            tooltipProps={getAddBtnTitle()}
                            disabled={!mayManageFile || isOverlimit || isExpired}
                        >
                            <i className="fa-solid fa-plus" />
                        </SecondaryButton>
                    </Box>
                    <FileExplorerHeader
                        selectedGroupId={props.id}
                        includeParentFiles={params.includeParentFiles}
                        onIncludeParentFilesChange={onIncludeParentFilesChange}
                    />

                    <PaneElement.TableLayout contentDeps={[table.getRowModel().rows.length]}>
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <CustomTableHeader
                                                key={header.id}
                                                sortCriteria={params.sortCriteria}
                                                sortDirection={params.sortDirection}
                                                onSort={onSort}
                                                {...header}
                                            >
                                                {/* TODO: fill out TagFilter after the tag filtering is implemented on the backend. */}
                                                {header.id === "filesTags" ? (
                                                    <TagFilter tags={[]} filters={[]} onChange={() => {}} />
                                                ) : null}
                                            </CustomTableHeader>
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>

                            <TableBody<FileInfoWithVersions>
                                table={table}
                                noDataMessage={t("sidepanels:groupPanel.table.noFiles")}
                                loading={tableLoading}
                                error={{ isError, onError: refetch }}
                            >
                                {({ rows }) =>
                                    rows.map((row) => (
                                        <CollapsibleRow key={row.id}>
                                            <CollapsibleSummary disabled={row.original.olderVersions.length === 0} row={row}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <React.Fragment key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </React.Fragment>
                                                ))}
                                            </CollapsibleSummary>
                                            <CollapsibleRows>
                                                {row.original.olderVersions.map((rowItem) => (
                                                    <TableRow key={rowItem.id}>
                                                        <MinimalTableCell />
                                                        <FileNameCell file={rowItem} />
                                                        <SourceCell file={rowItem} selectedGroupId={props.id} />
                                                        <FileTabTagsCell file={rowItem} />
                                                        <MinimalTableCell style={{ overflow: "visible" }}>
                                                            <ActionsCell file={rowItem} isOlderVersion />
                                                        </MinimalTableCell>
                                                    </TableRow>
                                                ))}
                                            </CollapsibleRows>
                                        </CollapsibleRow>
                                    ))
                                }
                            </TableBody>
                        </MinimalTable>
                    </PaneElement.TableLayout>

                    <FileUploadModal
                        open={isDialogOpen}
                        onClose={() => setIsDialogOpen(false)}
                        groupId={props.id}
                        groupName={props.name}
                    />
                </PaneElement.Container>
            </GroupDevicesProvider>
        </UploadProvider>
    );
};

export default FileTab;
